// src/hooks/useSquadNotifications.js
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { subscribeToChanges } from '../lib/realtimeSubscriptions';

/**
 * Hook to track unread squad-related notifications
 * Returns count of: pending friend requests + pending squad join requests for user's squads
 */
export const useSquadNotifications = (userId) => {
  const [notificationCount, setNotificationCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setNotificationCount(0);
      setLoading(false);
      return;
    }

    let active = true;
    const fetchNotificationCount = async () => {
      try {
        // Count pending friend requests received
        const { count: friendRequestCount, error: friendError } = await supabase
          .from('friend_requests')
          .select('*', { count: 'exact', head: true })
          .eq('receiver_id', userId)
          .eq('status', 'pending');

        if (friendError) throw friendError;

        // Get squads where user is creator or admin
        const { data: adminSquads, error: squadsError } = await supabase
          .from('squad_members')
          .select('squad_id')
          .eq('user_id', userId)
          .in('role', ['creator', 'admin']);

        if (squadsError) throw squadsError;

        const squadIds = adminSquads?.map(s => s.squad_id) || [];

        let joinRequestCount = 0;
        if (squadIds.length > 0) {
          // Count pending join requests for squads user manages
          const { count, error: joinError } = await supabase
            .from('squad_join_requests')
            .select('*', { count: 'exact', head: true })
            .in('squad_id', squadIds)
            .eq('status', 'pending');

          if (joinError) throw joinError;
          joinRequestCount = count || 0;
        }

        if (active) {
          setNotificationCount((friendRequestCount || 0) + joinRequestCount);
          setLoading(false);
        }
      } catch (error) {
        if (active) {
          setNotificationCount(0);
          setLoading(false);
        }
      }
    };

    fetchNotificationCount();

    // Real-time subscription for friend requests
    const unsubscribeFriends = subscribeToChanges(supabase, 'friend_requests_changes',
        {
          event: '*',
          schema: 'public',
          table: 'friend_requests',
          filter: `receiver_id=eq.${userId}`
        },
        () => {
          fetchNotificationCount();
        }
      );

    // Real-time subscription for squad join requests
    const unsubscribeJoins = subscribeToChanges(supabase, 'squad_join_requests_changes',
        {
          event: '*',
          schema: 'public',
          table: 'squad_join_requests'
        },
        () => {
          fetchNotificationCount();
        }
      );

    return () => {
      active = false;
      unsubscribeFriends();
      unsubscribeJoins();
    };
  }, [userId]);

  return { notificationCount, loading };
};

export default useSquadNotifications;
